import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, rupiah, slugify, fileUrl } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { DepartureBoard } from "@/components/DepartureBoard";
import { BookingDialog } from "@/components/BookingDialog";
import { SearchBar } from "@/components/SearchBar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  ChevronRight, Star, Quote, Wallet, CalendarClock, Users, Car,
  MapPin, ArrowRight, Route as RouteIcon, HelpCircle, ShieldCheck, Clock,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import NotFound from "@/pages/NotFound";

const TIME_GROUPS = [
  { key: "pagi", label: "Pagi", range: "05:00 – 10:59", lo: 5, hi: 10 },
  { key: "siang", label: "Siang", range: "11:00 – 14:59", lo: 11, hi: 14 },
  { key: "sore", label: "Sore", range: "15:00 – 17:59", lo: 15, hi: 17 },
  { key: "malam", label: "Malam", range: "18:00 – 04:59", lo: 18, hi: 4 },
];

const hourOf = (t) => { try { return parseInt(String(t).split(":")[0], 10); } catch { return 0; } };
const inGroup = (t, g) => { const h = hourOf(t); return g.key === "malam" ? (h >= 18 || h < 5) : (h >= g.lo && h <= g.hi); };
const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "");

const SectionHead = ({ children, id }) => (
  <h2 id={id} className="mb-4 mt-12 scroll-mt-24 font-display text-2xl font-bold tracking-tight">{children}</h2>
);

export default function RoutePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    api.get(`/routes/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const operators = useMemo(() => {
    if (!data) return [];
    const m = new Map();
    for (const s of data.schedules || []) {
      const o = m.get(s.driver_id) || {
        driver_id: s.driver_id, name: s.driver_name, verified: s.driver_verified,
        rating: s.driver_rating, reviews: s.driver_reviews, minPrice: s.price,
        times: new Set(), vehicles: new Set(),
      };
      o.minPrice = Math.min(o.minPrice, s.price);
      if (s.time) o.times.add(s.time);
      if (s.vehicle_name) o.vehicles.add(s.vehicle_name);
      m.set(s.driver_id, o);
    }
    return [...m.values()].sort((a, b) => b.rating - a.rating);
  }, [data]);

  if (notFound) return <NotFound message="Halaman rute tidak ditemukan." />;

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-7xl px-4 py-20 text-muted-foreground">Memuat...</div>
      </div>
    );
  }

  const { origin, destination, service_type: svc, service_label: label, per_unit } = data;
  const pfx = svc === "charter" ? "charter-drop-off" : "travel";
  const svcWord = svc === "charter" ? "Charter Drop Off" : "Travel";
  const sl = (o, d) => `/${pfx}-${slugify(o)}-${slugify(d)}`;
  const hasSchedules = (data.schedules || []).length > 0;
  const R = origin, D = destination;

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={data.title}
        description={data.meta_description}
        canonical={data.canonical}
        noindex={data.noindex}
        schema={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: (data.faq || []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }}
      />
      <Navbar />
      <Breadcrumbs
        items={[
          { name: "Beranda", path: "/" },
          { name: svc === "charter" ? "Charter Drop Off" : "Travel Door to Door", path: "/rute" },
          { name: `${svcWord} dari ${R}`, path: `/travel-dari-${slugify(R)}` },
          { name: `${svcWord} ${R} ${D}` },
        ]}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {/* ABOVE THE FOLD */}
        <p className="font-mono-terminal text-xs uppercase tracking-widest text-primary">{label}</p>
        <h1 className="mt-2 max-w-3xl font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl lg:text-5xl" data-testid="route-h1">
          {svcWord} {R} <span className="text-primary">{D}</span>
        </h1>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          Temukan jadwal {label} {R} {D} dari berbagai mitra travel. Bandingkan jadwal, harga,
          armada dan ketersediaan kursi sebelum melakukan pemesanan.
        </p>

        {/* Quick facts */}
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="route-quick-facts">
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> Harga mulai</div>
            <div className="mt-1 font-mono-terminal text-lg font-bold text-primary" data-testid="fact-price">{data.min_price ? rupiah(data.min_price) : "-"}</div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarClock className="h-3.5 w-3.5" /> Jadwal aktif</div>
            <div className="mt-1 font-mono-terminal text-lg font-bold" data-testid="fact-schedules">{data.schedule_count}</div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" /> Mitra tersedia</div>
            <div className="mt-1 font-mono-terminal text-lg font-bold" data-testid="fact-operators">{data.active_operators}</div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Berikutnya</div>
            <div className="mt-1 font-mono-terminal text-sm font-bold" data-testid="fact-next">
              {data.next_departure ? `${fmtDate(data.next_departure.date)} · ${data.next_departure.time}` : "-"}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <a href="#jadwal">
            <Button className="bg-primary font-bold text-primary-foreground hover:bg-primary/90" data-testid="route-cta-lihat-jadwal">
              Lihat Jadwal <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </a>
        </div>

        {/* Booking / search box */}
        <div className="mt-6">
          <SearchBar initialOrigin={R} initialDestination={D} initialService={svc} />
        </div>

        {/* JADWAL */}
        <SectionHead id="jadwal">Jadwal {label} {R} {D}</SectionHead>
        <p className="-mt-2 mb-4 text-sm text-muted-foreground">
          Data jadwal diperbarui berdasarkan keberangkatan yang dimasukkan oleh mitra travel.
        </p>
        {hasSchedules ? (
          <DepartureBoard schedules={data.schedules} onBook={setBooking} showRoute={false} />
        ) : (
          <div className="border border-border bg-card p-8 text-center" data-testid="route-empty-state">
            <p className="text-muted-foreground">
              Belum ada jadwal keberangkatan aktif untuk {label} {R} {D} pada tanggal yang dipilih.
              Silakan pilih tanggal lain atau lihat rute terkait.
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              <Link to={`/cari?origin=${encodeURIComponent(R)}&destination=${encodeURIComponent(D)}&service_type=${svc}`}>
                <Button variant="outline" data-testid="empty-pick-date">Pilih Tanggal Lain</Button>
              </Link>
              <Link to={`/travel-dari-${slugify(R)}`}>
                <Button variant="outline" data-testid="empty-other-routes">Lihat Rute Lain</Button>
              </Link>
            </div>
          </div>
        )}

        {/* HARGA */}
        {hasSchedules && (
          <>
            <SectionHead>Harga {label} {R} {D}</SectionHead>
            <p className="-mt-2 mb-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              Harga {label} {R} {D} dapat berbeda berdasarkan mitra travel, jadwal keberangkatan, jenis armada,
              titik penjemputan dan tujuan pengantaran.
            </p>
            <div className="overflow-x-auto border border-border bg-card">
              <table className="w-full text-sm" data-testid="route-price-table">
                <thead className="bg-black/40 font-mono-terminal text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">Travel</th>
                    <th className="px-4 py-2 text-left">Jadwal</th>
                    <th className="px-4 py-2 text-left">Armada</th>
                    <th className="px-4 py-2 text-right">Harga</th>
                  </tr>
                </thead>
                <tbody>
                  {data.schedules.slice(0, 15).map((s) => (
                    <tr key={s.id} className="border-t border-border">
                      <td className="px-4 py-2 font-semibold">{s.driver_name}</td>
                      <td className="px-4 py-2 font-mono-terminal">{s.time}</td>
                      <td className="px-4 py-2 text-muted-foreground">{s.vehicle_name || "-"}</td>
                      <td className="px-4 py-2 text-right font-mono-terminal font-bold text-primary">{rupiah(s.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.min_price && (
              <p className="mt-2 text-sm text-muted-foreground">
                Harga termurah yang tersedia saat ini mulai <span className="font-semibold text-primary">{rupiah(data.min_price)}</span> {per_unit}.
              </p>
            )}
          </>
        )}

        {/* PILIHAN MITRA */}
        {operators.length > 0 && (
          <>
            <SectionHead>Pilihan {svcWord} {R} {D}</SectionHead>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="route-operators">
              {operators.slice(0, 9).map((o) => (
                <div key={o.driver_id} className="border border-border bg-card p-5" data-testid={`operator-${o.driver_id}`}>
                  <div className="flex items-center gap-1.5">
                    <Link to={`/driver/${slugify(o.name)}`} className="font-display font-bold hover:text-primary hover:underline">{o.name}</Link>
                    {o.verified && <ShieldCheck className="h-4 w-4 text-emerald-400" />}
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <Star className="h-3 w-3 fill-primary text-primary" /> {o.rating || "Baru"}
                    {o.reviews > 0 && <span>({o.reviews} ulasan)</span>}
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">Armada: {[...o.vehicles].slice(0, 3).join(", ") || "-"}</div>
                  <div className="mt-1 text-sm">Harga mulai <span className="font-bold text-primary">{rupiah(o.minPrice)}</span></div>
                  <div className="mt-3 flex gap-2">
                    <Link to={`/driver/${slugify(o.name)}`}>
                      <Button variant="outline" size="sm" data-testid={`operator-profile-${o.driver_id}`}>Lihat Profil</Button>
                    </Link>
                    <a href="#jadwal"><Button size="sm" className="bg-primary text-primary-foreground" data-testid={`operator-schedule-${o.driver_id}`}>Lihat Jadwal</Button></a>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* DOOR TO DOOR / CHARTER */}
        {svc === "charter" ? (
          <>
            <SectionHead>Charter Drop Off {R} {D}</SectionHead>
            <div className="max-w-3xl space-y-3 text-sm leading-relaxed text-muted-foreground" data-testid="route-info-section">
              <p>
                Charter Drop Off {R} {D} menyewakan satu kendaraan penuh untuk perjalanan pribadi atau rombongan,
                dengan titik penjemputan fleksibel dari {R} dan pengantaran langsung ke tujuan di {D}.
              </p>
              <p>
                Tarif dihitung per mobil (bukan per penumpang) sehingga cocok untuk keluarga besar maupun acara.
                Bandingkan pilihan armada dan harga dari beberapa mitra, lalu pastikan titik jemput dan tujuan saat memesan.
              </p>
              <p>
                Travele menghubungkan Anda dengan mitra travel yang melayani carter rute {R} menuju {D}.
                Ketersediaan armada mengikuti data jadwal aktual masing-masing mitra.
              </p>
            </div>
          </>
        ) : (
          <>
            <SectionHead>Travel {R} {D} Door to Door</SectionHead>
            <div className="max-w-3xl space-y-3 text-sm leading-relaxed text-muted-foreground" data-testid="route-info-section">
              <p>
                Layanan {label} {R} {D} bekerja secara door to door: armada menjemput Anda langsung dari alamat
                di {R} dan mengantar sampai titik tujuan di {D}, tanpa perlu transit ke pool atau terminal.
              </p>
              <p>
                Setiap mitra travel memiliki jam keberangkatan dan cakupan area penjemputan yang berbeda. Bandingkan
                pilihan jadwal, armada dan harga dari beberapa mitra terlebih dahulu, lalu pastikan alamat lengkap
                penjemputan dan pengantaran saat melakukan pemesanan agar perjalanan berjalan nyaman.
              </p>
              <p>
                Travele menghubungkan Anda dengan mitra travel yang melayani rute {R} menuju {D}. Ketersediaan
                layanan tertentu mengikuti data jadwal aktual dari masing-masing mitra.
              </p>
            </div>
          </>
        )}

        {/* JAM KEBERANGKATAN */}
        {hasSchedules && (
          <>
            <SectionHead>Pilihan Jam Keberangkatan {svcWord} {R} {D}</SectionHead>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="route-time-groups">
              {TIME_GROUPS.map((g) => {
                const rows = (data.schedules || []).filter((s) => inGroup(s.time, g));
                if (rows.length === 0) return null;
                return (
                  <div key={g.key} className="border border-border bg-card p-4" data-testid={`time-group-${g.key}`}>
                    <h3 className="font-display font-bold">{svcWord} {R} {D} {g.label}</h3>
                    <div className="text-[11px] text-muted-foreground">{g.range} WIB · {rows.length} jadwal</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {[...new Set(rows.map((s) => s.time))].sort().slice(0, 8).map((t) => (
                        <span key={t} className="rounded-full border border-border px-2 py-0.5 font-mono-terminal text-xs">{t}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ARMADA */}
        {data.vehicles?.length > 0 && (
          <>
            <SectionHead>Armada {svcWord} {R} {D}</SectionHead>
            <div className="flex flex-wrap gap-2" data-testid="route-vehicles">
              {data.vehicles.map((v) => (
                <span key={v} className="flex items-center gap-1.5 border border-border bg-card px-3 py-1.5 text-sm">
                  <Car className="h-4 w-4 text-primary" /> {v}
                </span>
              ))}
            </div>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Jenis armada di atas berasal dari jadwal aktif mitra travel pada rute {R} – {D}. Kapasitas dan fasilitas
              dapat berbeda tiap kendaraan.
            </p>
          </>
        )}

        {/* AREA JEMPUT / ANTAR */}
        <div className="mt-12 grid gap-4 md:grid-cols-2">
          <div className="border border-border bg-card p-5" data-testid="route-pickup-areas">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold"><MapPin className="h-4 w-4 text-primary" /> Area Penjemputan di {R}</h2>
            {data.pickup_areas?.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {data.pickup_areas.map((a) => <span key={a} className="rounded-full border border-border px-3 py-1 text-xs">{a}</span>)}
              </div>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                Cakupan penjemputan dapat berbeda pada setiap mitra travel. Pastikan alamat lengkap di {R} saat melakukan pemesanan.
              </p>
            )}
          </div>
          <div className="border border-border bg-card p-5" data-testid="route-dropoff-areas">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold"><MapPin className="h-4 w-4 text-primary" /> Area Pengantaran di {D}</h2>
            {data.dropoff_areas?.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {data.dropoff_areas.map((a) => <span key={a} className="rounded-full border border-border px-3 py-1 text-xs">{a}</span>)}
              </div>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                Cakupan pengantaran dapat berbeda pada setiap mitra travel. Pastikan alamat lengkap di {D} saat melakukan pemesanan.
              </p>
            )}
          </div>
        </div>

        {/* INFO PERJALANAN */}
        <SectionHead>Informasi Perjalanan {R} ke {D}</SectionHead>
        <div className="grid gap-3 sm:grid-cols-2" data-testid="route-trip-info">
          <div className="border border-border bg-card px-4 py-3 text-sm"><span className="text-muted-foreground">Rute</span><div className="font-semibold">{R} → {D}</div></div>
          <div className="border border-border bg-card px-4 py-3 text-sm"><span className="text-muted-foreground">Jenis layanan</span><div className="font-semibold">{label}</div></div>
          <div className="border border-border bg-card px-4 py-3 text-sm"><span className="text-muted-foreground">Armada tersedia</span><div className="font-semibold">{data.vehicles?.length ? data.vehicles.join(", ") : "Mengikuti ketersediaan mitra"}</div></div>
          <div className="border border-border bg-card px-4 py-3 text-sm"><span className="text-muted-foreground">Mitra melayani</span><div className="font-semibold">{data.active_operators} mitra travel</div></div>
        </div>

        {/* CARA PESAN */}
        <SectionHead>Cara Pesan {svcWord} {R} {D}</SectionHead>
        <ol className="max-w-3xl list-decimal space-y-2 pl-5 text-sm text-muted-foreground" data-testid="route-how-to">
          <li>Masukkan kota asal {R} dan tujuan {D} pada kolom pencarian.</li>
          <li>Tentukan tanggal keberangkatan dan jumlah penumpang.</li>
          <li>Lihat jadwal travel yang tersedia beserta harga dan armada.</li>
          <li>Bandingkan pilihan mitra travel, jadwal, dan harga.</li>
          <li>Pilih keberangkatan lalu isi data pemesanan.</li>
          <li>Ikuti proses konfirmasi sesuai sistem Travele (via WhatsApp Admin).</li>
        </ol>
        <div className="mt-4"><a href="#jadwal"><Button className="bg-primary font-bold text-primary-foreground" data-testid="route-how-cta">Cari Jadwal {svcWord}</Button></a></div>

        {/* TENTANG */}
        <SectionHead>Cari {svcWord} {R} {D} di Travele</SectionHead>
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Travele membantu calon penumpang menemukan pilihan perjalanan dari berbagai mitra travel dalam satu
          platform. Informasi jadwal, rute, harga dan armada dapat dibandingkan sebelum Anda menentukan pilihan
          perjalanan {R} menuju {D}.
        </p>

        {/* RUTE TERKAIT DARI ORIGIN */}
        {data.related?.length > 0 && (
          <>
            <SectionHead>Rute {svcWord} Lain dari {R}</SectionHead>
            <div className="flex flex-wrap gap-2" data-testid="route-related-from">
              {data.related.map((d) => (
                <Link key={d} to={sl(R, d)} className="flex items-center gap-1 border border-border bg-card px-4 py-2 text-sm transition-colors hover:border-primary">
                  {svcWord} {R} {d} <ArrowRight className="h-3.5 w-3.5 text-primary" />
                </Link>
              ))}
            </div>
          </>
        )}

        {/* RUTE MENUJU DESTINATION */}
        {data.related_to?.length > 0 && (
          <>
            <SectionHead>{svcWord} Menuju {D}</SectionHead>
            <div className="flex flex-wrap gap-2" data-testid="route-related-to">
              {data.related_to.map((o) => (
                <Link key={o} to={sl(o, D)} className="flex items-center gap-1 border border-border bg-card px-4 py-2 text-sm transition-colors hover:border-primary">
                  <RouteIcon className="h-3.5 w-3.5 text-primary" /> {svcWord} {o} {D}
                </Link>
              ))}
            </div>
          </>
        )}

        {/* ARTIKEL PENDUKUNG */}
        {data.articles?.length > 0 && (
          <>
            <SectionHead>Panduan Perjalanan {R} – {D}</SectionHead>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="route-articles">
              {data.articles.map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`} className="group border border-border bg-card p-4 transition-colors hover:border-primary" data-testid={`route-article-${a.slug}`}>
                  {a.cover_image && <img src={fileUrl(a.cover_image)} alt={a.title} loading="lazy" className="mb-3 h-32 w-full object-cover" />}
                  <div className="font-display font-bold leading-tight group-hover:text-primary">{a.title}</div>
                  {a.excerpt && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{a.excerpt}</p>}
                </Link>
              ))}
            </div>
          </>
        )}

        {/* FAQ */}
        <SectionHead>Pertanyaan Seputar {svcWord} {R} {D}</SectionHead>
        <Accordion type="single" collapsible className="border border-border bg-card px-4">
          {(data.faq || []).map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left"><span className="flex items-start gap-2"><HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{f.q}</span></AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Ulasan */}
        {data.reviews?.length > 0 && (
          <>
            <SectionHead>Ulasan Penumpang</SectionHead>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {data.reviews.map((rv, i) => (
                <div key={i} className="border border-border bg-card p-5" data-testid={`route-review-${i}`}>
                  <Quote className="h-5 w-5 text-primary/50" />
                  <div className="mt-2 flex items-center gap-1 text-primary">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className={`h-3.5 w-3.5 ${s < Math.round(rv.average) ? "fill-primary" : "text-muted-foreground"}`} />
                    ))}
                    <span className="ml-1 font-mono-terminal text-xs text-muted-foreground">{rv.average?.toFixed(1)}</span>
                  </div>
                  {rv.comment && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">"{rv.comment}"</p>}
                  <div className="mt-3 text-sm font-semibold">{rv.passenger_name}</div>
                  <div className="font-mono-terminal text-[10px] uppercase text-muted-foreground">{rv.driver_name}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* FINAL CTA */}
        <div className="mt-14 border border-primary/40 bg-primary/5 p-8 text-center" data-testid="route-final-cta">
          <h2 className="font-display text-2xl font-bold">Cari Jadwal {svcWord} {R} {D}</h2>
          <p className="mt-2 text-sm text-muted-foreground">Temukan jadwal perjalanan yang sesuai dengan kebutuhan Anda.</p>
          <a href="#jadwal"><Button className="mt-4 bg-primary px-8 font-bold text-primary-foreground hover:bg-primary/90" data-testid="route-final-cta-btn">Lihat Jadwal Tersedia</Button></a>
        </div>
      </div>

      <Footer />
      <WhatsAppFloat />
      <BookingDialog schedule={booking} open={!!booking} onClose={() => setBooking(null)} />
    </div>
  );
}
